import { useLayoutEffect, useState } from 'react';
import { ValuesType } from 'utility-types';

import Listener from '~/modules/utils/Listener';
import storage from '~/modules/utils/storage';

export class Setting<T> extends Listener<[T]> {
  private value: T | undefined = undefined;

  public constructor(
    private name: string,
    private defaultValue: T,
    private driver: 'local' | 'session' | 'memory' = 'local',
  ) {
    super();
  }

  public get = (): T => {
    if (this.value === undefined) {
      this.value = storage[this.driver].getItem(`settings-${this.name}`)! ?? this.defaultValue;
    }
    return this.value as T;
  };

  public set = (newValue: T) => {
    this.value = newValue;

    storage[this.driver].setItem(`settings-${this.name}`, newValue);
    this.onUpdate(newValue);
  };
}

export const GraphicsLevel = ['high', 'low'] as const;
const GRAPHICS_LEVEL_KEY = 'graphics-level';
export const GraphicSetting = new Setting<ValuesType<typeof GraphicsLevel>>(GRAPHICS_LEVEL_KEY, GraphicsLevel[0]);

export const VisualThemes = ['light', 'dark'] as const;
export type VisualTheme = ValuesType<typeof VisualThemes>;
export const VisualThemeSetting = new Setting<VisualTheme>('visual-theme', 'light');

export function useSettingValue<T>(value: Setting<T>) {
  const [currentValue, setCurrentValue] = useState(() => value.get());

  useLayoutEffect(() => {
    return value.addListener(setCurrentValue);
  }, [value]);

  return [currentValue, value.set] as const;
}
